/**
 * Loads media marked for deferred loading only when it is about to scroll into view. The browser's own lazy
 * loading starts a screen or two early on phones, so large media further down the page competed with the
 * product images at the top.
 *
 * - Images (photo-slot snippet): data-defer-src / data-defer-srcset become src / srcset.
 * - Videos (deferred-video snippet): the poster and <source> URLs are filled in, then the muted clip plays
 *   while it is on screen and pauses when it scrolls away. With reduced motion it shows play controls instead.
 */
// Start a little before the media reaches the screen, so big files have time to arrive
const MARGIN = '600px 0px';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {HTMLImageElement} img */
function loadImage(img) {
  const { deferSrcset, deferSrc } = img.dataset;
  if (deferSrcset) img.srcset = deferSrcset;
  if (deferSrc) img.src = deferSrc;
  img.removeAttribute('data-defer-srcset');
  img.removeAttribute('data-defer-src');
}

/** Plays videos while they are visible, pauses them otherwise. */
const playback =
  'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) continue;
          if (entry.isIntersecting && !reduceMotion.matches) video.play().catch(() => {});
          else video.pause();
        }
      })
    : null;

/** @param {HTMLVideoElement} video */
function loadVideo(video) {
  if (video.dataset.deferPoster) video.poster = video.dataset.deferPoster;
  for (const source of video.querySelectorAll('source[data-defer-src]')) {
    if (source instanceof HTMLSourceElement && source.dataset.deferSrc) source.src = source.dataset.deferSrc;
    source.removeAttribute('data-defer-src');
  }
  video.removeAttribute('data-defer-video');
  video.removeAttribute('data-defer-poster');
  video.load();

  if (reduceMotion.matches) {
    video.controls = true;
  } else if (playback) {
    playback.observe(video);
  } else {
    video.play().catch(() => {});
  }
}

/** @param {Element} element */
function load(element) {
  if (element instanceof HTMLImageElement) loadImage(element);
  else if (element instanceof HTMLVideoElement) loadVideo(element);
}

const observer =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer?.unobserve(entry.target);
            load(entry.target);
          }
        },
        { rootMargin: MARGIN }
      )
    : null;

/** @param {ParentNode} root */
function watch(root) {
  for (const element of root.querySelectorAll('img[data-defer-src], img[data-defer-srcset], video[data-defer-video]')) {
    if (observer) observer.observe(element);
    else load(element);
  }
}

if (!window.holdraDeferredMedia) {
  window.holdraDeferredMedia = true;
  watch(document);
  // Sections re-rendered in the theme editor bring new media
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) watch(event.target);
  });
}
